import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeFileUrl } from '@/utils/fileUrl';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Phone,
  Mail,
  Clock,
  FileText,
  Award,
  Shield,
  Upload,
  Loader2,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Zap
} from 'lucide-react';

// Time zone labels
const TIME_ZONE_LABELS = {
  'America/New_York': 'Eastern (EST/EDT)',
  'America/Chicago': 'Central (CST/CDT)',
  'America/Denver': 'Mountain (MST/MDT)',
  'America/Los_Angeles': 'Pacific (PST/PDT)',
  'America/Anchorage': 'Alaska (AKST/AKDT)',
  'Pacific/Honolulu': 'Hawaii (HST)'
};

// Facility type labels
const FACILITY_TYPE_LABELS = {
  'DEDICATED_MINI_SHOP': 'Dedicated Mini Shop',
  'MOBILE_RAILCAR_REPAIR': 'Mobile Railcar Repair',
  'FULL_SERVICE_HOME_SHOP': 'Full Service Home Shop'
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '-';
  }
};

const FacilityDetailPage = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [facility, setFacility] = useState(null);
  const [certification, setCertification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [uploadingLetter, setUploadingLetter] = useState(false);
  const [savingAutoLog, setSavingAutoLog] = useState(false);

  const certificateInputRef = useRef(null);
  const letterInputRef = useRef(null);

  const userRole = user?.role || 'TECHNICIAN';
  const canViewCertification = ['OPERATOR', 'MANAGER', 'ADMIN'].includes(userRole);
  const canUploadCertification = ['MANAGER', 'ADMIN'].includes(userRole);
  // Phase 3 — Welder Maintenance Auto Log. Admin-only toggle; everyone else
  // who can see the facility detail page sees the current state read-only.
  const canToggleAutoLog = userRole === 'ADMIN';
  const autoLogEnabled = !!facility?.welding_auto_log_enabled;

  const fetchFacility = useCallback(async () => {
    try {
      const response = await api.get(`/facilities/${facilityId}`);
      setFacility(response.data);
    } catch (error) {
      console.error('Failed to fetch facility:', error);
      toast.error('Failed to load facility details');
      navigate('/facility-management');
    } finally {
      setLoading(false);
    }
  }, [api, facilityId, navigate]);

  const fetchCertification = useCallback(async () => {
    try {
      const response = await api.get(`/facilities/${facilityId}/certification`);
      setCertification(response.data);
    } catch (error) {
      console.error('Failed to fetch certification:', error);
      // Don't show error toast for 403 - technicians won't have access
      if (error.response?.status !== 403) {
        toast.error('Failed to load certification data');
      }
    }
  }, [api, facilityId]);

  useEffect(() => {
    fetchFacility();
    if (canViewCertification) {
      fetchCertification();
    }
  }, [facilityId, canViewCertification, fetchFacility, fetchCertification]);

  const handleCertificateUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate PDF
    if (!file.name.toLowerCase().endsWith('.pdf') || file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed for certification documents');
      return;
    }

    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploadingCertificate(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/facilities/${facilityId}/certificate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('AAR Certificate uploaded successfully');
      fetchCertification();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload certificate';
      toast.error(message);
    } finally {
      setUploadingCertificate(false);
      // Reset input
      if (certificateInputRef.current) {
        certificateInputRef.current.value = '';
      }
    }
  };

  const handleLetterUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate PDF
    if (!file.name.toLowerCase().endsWith('.pdf') || file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed for certification documents');
      return;
    }

    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploadingLetter(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/facilities/${facilityId}/letter`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Letter of Compliance uploaded successfully');
      fetchCertification();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload letter';
      toast.error(message);
    } finally {
      setUploadingLetter(false);
      // Reset input
      if (letterInputRef.current) {
        letterInputRef.current.value = '';
      }
    }
  };

  const openDocument = (url) => {
    if (url) {
      window.open(normalizeFileUrl(url), '_blank', 'noopener,noreferrer');
    }
  };

  // Phase 3 — Welder Maintenance Auto Log toggle. Admin-only. Persists on the
  // facility doc; backend reads it during WS CLOSED transitions.
  const handleToggleAutoLog = async () => {
    if (!canToggleAutoLog || savingAutoLog) return;
    const next = !autoLogEnabled;
    setSavingAutoLog(true);
    try {
      await api.put(`/facilities/${facilityId}`, {
        welding_auto_log_enabled: next,
      });
      setFacility((prev) => (prev ? { ...prev, welding_auto_log_enabled: next } : prev));
      toast.success(
        next
          ? 'Welder Maintenance Auto Log turned ON for this facility'
          : 'Welder Maintenance Auto Log turned OFF for this facility'
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update Auto Log setting');
    } finally {
      setSavingAutoLog(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="p-8 text-center">
        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-[#323033] mb-2">Facility not found</h2>
        <Button onClick={() => navigate('/facility-management')}>
          Back to Facility Management
        </Button>
      </div>
    );
  }

  const hasCertificate = certification?.certificate != null;
  const hasLetter = certification?.letter != null;
  const hasAnyCertification = hasCertificate || hasLetter;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="facility-detail-page">
      {/* Header with Back button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/facility-management')}
          data-testid="back-to-facilities"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-[#323033]" data-testid="facility-name">
              {facility.name}
            </h1>
            <span
              className="px-2 py-1 text-sm font-mono font-medium bg-[#005EF8]/10 text-[#005EF8] rounded"
              data-testid="facility-code-badge"
              title="Facility ID"
            >
              {facility.facility_code || 'Not set'}
            </span>
            {facility.station_stencil && (
              <span className="px-2 py-1 text-sm font-medium bg-gray-100 text-[#5A5A5A] rounded" title="Station Stencil">
                {facility.station_stencil}
              </span>
            )}
          </div>
          <p className="text-[#5A5A5A] mt-1">
            {FACILITY_TYPE_LABELS[facility.facility_type] || facility.facility_type}
            {facility.service_client_region && ` • ${facility.service_client_region}`}
          </p>
        </div>
        {canToggleAutoLog ? (
          <Button
            type="button"
            onClick={handleToggleAutoLog}
            disabled={savingAutoLog}
            className={
              autoLogEnabled
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }
            data-testid="welding-auto-log-toggle"
            title={
              autoLogEnabled
                ? 'Auto Log is ON. When BRCs are closed, the system checks once for qualifying welding data and may auto-create a maintenance log.'
                : 'Auto Log is OFF. No automatic welder maintenance logs will be created from closed BRCs.'
            }
          >
            {savingAutoLog ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Welder Maintenance Auto Log: {autoLogEnabled ? 'ON' : 'OFF'}
          </Button>
        ) : (
          <span
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md ${
              autoLogEnabled
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}
            data-testid="welding-auto-log-status"
            title="Only Admins can change this setting."
          >
            <Zap className="w-4 h-4" />
            Auto Log: {autoLogEnabled ? 'ON' : 'OFF'}
          </span>
        )}
      </div>

      {/* Phase 3 — Auto Log helper text. Lives where the AAR Certification
          Documents prominent header used to be. Upload access remains lower on
          the page under the certification section. */}
      <Card className="border-emerald-100 bg-emerald-50/40" data-testid="auto-log-helper-card">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Zap className={`w-5 h-5 mt-0.5 ${autoLogEnabled ? 'text-emerald-600' : 'text-gray-400'}`} />
            <div className="space-y-1">
              <p className="font-medium text-[#323033]">Welder Maintenance Auto Log</p>
              <p className="text-sm text-[#5A5A5A]">
                {autoLogEnabled
                  ? 'ON — When future BRCs at this facility are closed, the system checks each one once for qualifying welding data and may auto-create a welder maintenance log entry if all validation rules pass.'
                  : 'OFF — Auto-logging is disabled for this facility. Welder maintenance logs are only created manually.'}
              </p>
              {!canToggleAutoLog && (
                <p className="text-xs text-[#5A5A5A] italic">
                  Only Admins can change this setting.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Facility Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Location & Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#005EF8]" />
              Location & Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#5A5A5A]">Facility ID</Label>
                <p className="font-mono text-[#323033]" data-testid="facility-detail-code">
                  {facility.facility_code || <span className="text-amber-600">Not set</span>}
                </p>
              </div>
              <div>
                <Label className="text-xs text-[#5A5A5A]">Station Stencil</Label>
                <p className="text-[#323033]" data-testid="facility-detail-stencil">
                  {facility.station_stencil || 'N/A'}
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#5A5A5A]">Address</Label>
              <p className="text-[#323033]">
                {facility.address}
                {facility.address_line_2 && <>, {facility.address_line_2}</>}
              </p>
              <p className="text-[#323033]">
                {facility.city}, {facility.state} {facility.zip_code}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#5A5A5A]">Time Zone</Label>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#5A5A5A]" />
                  <span>{TIME_ZONE_LABELS[facility.time_zone] || facility.time_zone}</span>
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <Label className="text-xs text-[#5A5A5A] mb-2 block">Primary Contact</Label>
              <p className="font-medium text-[#323033]">
                {facility.contact_first_name} {facility.contact_last_name}
              </p>
              <p className="text-sm text-[#5A5A5A]">{facility.contact_title}</p>
              <div className="flex items-center gap-2 mt-2">
                <Phone className="w-4 h-4 text-[#5A5A5A]" />
                <span className="text-sm">{facility.contact_phone}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-4 h-4 text-[#5A5A5A]" />
                <span className="text-sm">{facility.contact_email}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Capabilities */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#005EF8]" />
              Capabilities
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {facility.activity_codes?.length > 0 && (
              <div>
                <Label className="text-xs text-[#5A5A5A]">AAR Activity Codes</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {facility.activity_codes.map((code) => (
                    <span key={code} className="px-2 py-0.5 text-xs bg-gray-100 rounded">
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {facility.material_groups?.length > 0 && (
              <div>
                <Label className="text-xs text-[#5A5A5A]">Material Groups</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {facility.material_groups.map((mg) => (
                    <span key={mg} className="px-2 py-0.5 text-xs bg-gray-100 rounded">
                      {mg}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {facility.repair_types?.length > 0 && (
              <div>
                <Label className="text-xs text-[#5A5A5A]">Repair Types</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {facility.repair_types.map((type) => (
                    <span key={type} className="px-2 py-0.5 text-xs bg-gray-100 rounded">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {facility.cleaning_types?.length > 0 && (
              <div>
                <Label className="text-xs text-[#5A5A5A]">Cleaning Types</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {facility.cleaning_types.map((type) => (
                    <span key={type} className="px-2 py-0.5 text-xs bg-gray-100 rounded">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(facility.repair_level_primary || facility.repair_level_additional?.length > 0) && (
              <div>
                <Label className="text-xs text-[#5A5A5A]">Repair Levels</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {facility.repair_level_primary && (
                    <span className="px-2 py-0.5 text-xs bg-[#005EF8]/10 text-[#005EF8] rounded font-medium">
                      {facility.repair_level_primary}
                    </span>
                  )}
                  {facility.repair_level_additional?.map((level) => (
                    <span key={level} className="px-2 py-0.5 text-xs bg-gray-100 rounded">
                      {level}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* 2026-07-05 Wave 3 #7: Additional Notes (plain text). */}
            {facility.additional_notes && facility.additional_notes.trim() && (
              <div>
                <Label className="text-xs text-[#5A5A5A]">Additional Notes</Label>
                <div
                  className="mt-1 text-sm whitespace-pre-wrap text-[#323033]"
                  data-testid="facility-additional-notes-display"
                >
                  {facility.additional_notes}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Certification Documents Section - Full details with upload */}
      {canViewCertification && (
        <Card id="certification-section" data-testid="certification-section">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="w-4 h-4 text-[#005EF8]" />
              Certification Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Certificate Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    hasCertificate ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {hasCertificate ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-[#323033]">AAR Certificate</h4>
                    {hasCertificate ? (
                      <>
                        <p className="text-sm text-[#5A5A5A]">{certification.certificate.file_name}</p>
                        <p className="text-xs text-[#808080] mt-1">
                          Uploaded {formatDate(certification.certificate.uploaded_at)} by {certification.certificate.uploaded_by_name}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-[#5A5A5A]">No certificate uploaded</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasCertificate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDocument(certification.certificate.file_url)}
                      data-testid="view-certificate-detail"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  )}
                  {canUploadCertification && (
                    <>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        ref={certificateInputRef}
                        onChange={handleCertificateUpload}
                        className="hidden"
                        data-testid="certificate-file-input"
                      />
                      <Button
                        variant={hasCertificate ? "outline" : "default"}
                        size="sm"
                        onClick={() => certificateInputRef.current?.click()}
                        disabled={uploadingCertificate}
                        className={!hasCertificate ? "bg-[#005EF8] hover:bg-[#0047C2]" : ""}
                        data-testid="upload-certificate-btn"
                      >
                        {uploadingCertificate ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        {hasCertificate ? 'Replace' : 'Upload'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Letter of Compliance Section */}
            <div className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    hasLetter ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {hasLetter ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Shield className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-[#323033]">Letter of Compliance</h4>
                    {hasLetter ? (
                      <>
                        <p className="text-sm text-[#5A5A5A]">{certification.letter.file_name}</p>
                        <p className="text-xs text-[#808080] mt-1">
                          Uploaded {formatDate(certification.letter.uploaded_at)} by {certification.letter.uploaded_by_name}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-[#5A5A5A]">No letter of compliance uploaded</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasLetter && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDocument(certification.letter.file_url)}
                      data-testid="view-letter-detail"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  )}
                  {canUploadCertification && (
                    <>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        ref={letterInputRef}
                        onChange={handleLetterUpload}
                        className="hidden"
                        data-testid="letter-file-input"
                      />
                      <Button
                        variant={hasLetter ? "outline" : "default"}
                        size="sm"
                        onClick={() => letterInputRef.current?.click()}
                        disabled={uploadingLetter}
                        className={!hasLetter ? "bg-[#005EF8] hover:bg-[#0047C2]" : ""}
                        data-testid="upload-letter-btn"
                      >
                        {uploadingLetter ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        {hasLetter ? 'Replace' : 'Upload'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Info note for new facilities */}
            {!hasAnyCertification && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    Certification documents can be added once this facility is AAR certified.
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Only PDF files are accepted. Maximum file size: 10MB.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FacilityDetailPage;
